import { useCallback, useEffect, useMemo, useState } from 'react'

import { SUPPORT_RETENTION_DAYS } from '@onr/schema'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'
import dayjs from 'dayjs'

import { useAuthenticatedFirebaseUser } from '@/auth/useFirebaseUser'
import { useNow } from '@/hooks/useNow'

import { requesterOpenSupportRequestsQuery, requesterSettledSupportRequestsQuery } from './supportRequests'
import { type SupportRequestDocument } from './types'

const UNAVAILABLE_AFTER_MS = 10_000

export type SupportLoadState = 'loading' | 'ready' | 'unavailable'

interface SnapshotQuery {
  readonly isPending: boolean
  readonly isError: boolean
  readonly data?: {
    readonly isLoading?: boolean
    readonly hasError?: boolean
    readonly snapshot?: { readonly metadata: { readonly fromCache: boolean } }
  }
}

export const retentionStart = (now: Date) => dayjs(now).subtract(SUPPORT_RETENTION_DAYS, 'day').toDate()

// A listener whose backend went away after the first load keeps serving its cache without an error,
// so a snapshot still read from the cache after the delay counts as unavailable, like one that never came.
export const useLoadState = (queries: ReadonlyArray<SnapshotQuery>) => {
  const [timedOut, setTimedOut] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const waiting = queries.some(
    ({ isPending, data }) => isPending || Boolean(data?.isLoading) || Boolean(data?.snapshot?.metadata.fromCache)
  )
  const failed = queries.some(({ isError, data }) => isError || Boolean(data?.hasError))

  useEffect(() => {
    setTimedOut(false)
    if (!waiting) {
      return
    }
    const timer = setTimeout(() => setTimedOut(true), UNAVAILABLE_AFTER_MS)
    return () => clearTimeout(timer)
  }, [waiting, attempt])

  const restart = useCallback(() => setAttempt((count) => count + 1), [])

  const state: SupportLoadState = failed || timedOut ? 'unavailable' : waiting ? 'loading' : 'ready'
  return { state, restart }
}

export const useRequesterSupportRequests = () => {
  const { regionId } = useAuthenticatedFirebaseUser()
  const queryClient = useQueryClient()
  const now = useNow()
  const settledSince = useMemo(() => dayjs(retentionStart(new Date())).startOf('hour').toDate(), [])

  const open = useQuery(requesterOpenSupportRequestsQuery(regionId))
  const settled = useQuery(requesterSettledSupportRequestsQuery(regionId, settledSince))

  const requests = useMemo(() => {
    const cutoff = retentionStart(now)
    const byNumber = new Map<number, SupportRequestDocument>()
    for (const request of [...(open.data?.data ?? []), ...(settled.data?.data ?? [])]) {
      if (request.requesterStatus !== 'settled' || (request.settledAt && request.settledAt >= cutoff)) {
        byNumber.set(request.number, request)
      }
    }
    return [...byNumber.values()].sort((a, b) => b.lastActivity.at.getTime() - a.lastActivity.at.getTime())
  }, [open.data, settled.data, now])

  const { state: loadState, restart } = useLoadState([open, settled])

  const retry = useCallback(() => {
    restart()
    handleNotAwaitedPromise(queryClient.resetQueries({ queryKey: ['supportRequests', 'requester', regionId] }))
  }, [queryClient, regionId, restart])

  return { requests, loadState, retry }
}
